/**
 * Live HTML preview iframe with in-place body patching.
 *
 * Used ONLY while a create/update is actively streaming — see
 * `html-canvas.tsx`, which swaps to `HtmlStaticFrame` (a genuine `srcDoc`
 * load) the moment streaming finishes.
 *
 * Mounts a stable bootstrap document (Tailwind CDN + morphdom + a tiny
 * postMessage listener) exactly ONCE per artifact id. After bootstrap, all
 * subsequent content updates are sent as messages and morphdom patches
 * the body in place — the iframe never reloads, Tailwind never re-fetches,
 * scroll position is preserved, and there is no flicker between updates.
 * The trade-off, and the reason this component hands off to
 * `HtmlStaticFrame` once streaming ends: morphdom-inserted `<script>` tags
 * never execute (the same limitation a plain `innerHTML` assignment has),
 * so a chart or other script-driven behavior would render as permanently
 * inert while it's live here — acceptable mid-stream since the content
 * isn't finished anyway, not acceptable for the final artifact.
 *
 * The iframe is sandboxed without `allow-same-origin`, so the parent has
 * no DOM access — communication is one-way (parent → iframe) over
 * `postMessage`. The iframe sends one ready signal back when bootstrap is
 * complete; everything else flows top-down.
 *
 * @module components/artifacts/html-preview-frame
 */

import { useEffect, useRef } from "react";

interface HtmlPreviewFrameProps {
  /**
   * Stable id for the artifact being rendered. The iframe re-mounts when
   * this changes (different artifact opened) so streams can't bleed
   * across documents.
   */
  artifactId: string;
  /** Latest HTML body. Sent to the iframe via postMessage as it changes. */
  html: string;
  /** Used as the iframe `title` for accessibility tooling. */
  title: string;
  /**
   * Throttle window between body patches. Each patch is cheap (morphdom
   * diff over the body), so the throttle is mostly to coalesce rapid
   * stream tokens into one render frame.
   */
  throttleMs?: number;
  /**
   * Bump this counter to force a hard reload of the iframe (re-runs the
   * bootstrap shell and re-applies the latest HTML from scratch). Useful
   * for the toolbar's "Refresh preview" button when model output contains
   * scripts or stateful elements that morphdom can't fully reset.
   */
  reloadKey?: number;
  /**
   * Called once per bootstrap when the iframe has finished loading the
   * Tailwind shell + morphdom and is ready to accept HTML. The canvas uses
   * this to decide when it's safe to hide the loading skeleton — sending
   * content before this fires would just queue messages into a still-blank
   * iframe and the user would see a flash of empty white.
   */
  onReady?: () => void;
}

/**
 * The bootstrap document loaded into the iframe. Held as a constant so
 * React always passes the same `srcDoc` value — that prevents the iframe
 * from reloading when the parent re-renders.
 *
 * The inline script:
 *  1. Loads Tailwind CDN (handles classes the model emits).
 *  2. Loads morphdom from CDN (in-place DOM diff).
 *  3. Listens for `artifact-html` messages, parses incoming HTML, and
 *     morphs the body. Also copies any new `<style>` tags from the
 *     incoming `<head>` into the live head so model-supplied styles
 *     keep working.
 *  4. Posts an `artifact-bootstrap-ready` message back to the parent so
 *     the parent knows when it can start sending content.
 *  5. Posts an `artifact-first-paint` message AFTER the first successful
 *     applyHtml — the parent uses this (not bootstrap-ready) to lift the
 *     loading skeleton, so the user never sees an empty iframe between
 *     "ready to receive" and "actually painting content".
 *
 * Scripts in the AI's HTML do NOT execute here — morphdom won't run
 * inserted `<script>` tags, and innerHTML assignment likewise skips
 * script execution. That's an inherent limitation of in-place DOM
 * patching, not a deliberate restriction — see `HtmlStaticFrame` for the
 * post-stream render, which loads the same HTML as a genuine document so
 * scripts run normally.
 */
const BOOTSTRAP_SRCDOC = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <script src="https://cdn.tailwindcss.com"></script>
  <script src="https://cdn.jsdelivr.net/npm/morphdom@2.7.4/dist/morphdom-umd.min.js"></script>
  <style>
    html, body { margin: 0; padding: 0; }
    body { background: #ffffff; color: #0f172a; font-family: ui-sans-serif, system-ui, sans-serif; min-height: 100%; }
  </style>
  <script>
    (function () {
      var ready = false;
      var queued = null;
      var firstPaintSent = false;
      var copiedStyleTexts = new Set();
      // Tail-style auto-scroll: while the user is parked at (or near) the
      // bottom we keep them pinned to the latest content as the stream
      // grows. If they scroll up to read something mid-stream we back off
      // until they return to the bottom on their own.
      var autoScroll = true;
      var isProgrammaticScroll = false;
      var SCROLL_THRESHOLD_PX = 50;

      function isAtBottom() {
        var doc = document.documentElement;
        return (window.innerHeight + window.scrollY) >= (doc.scrollHeight - SCROLL_THRESHOLD_PX);
      }
      function scrollToBottom() {
        isProgrammaticScroll = true;
        var doc = document.documentElement;
        window.scrollTo(0, doc.scrollHeight);
        // Release the flag on the next tick so the listener can see real
        // user scrolls again.
        setTimeout(function () { isProgrammaticScroll = false; }, 0);
      }
      window.addEventListener('scroll', function () {
        if (isProgrammaticScroll) return;
        autoScroll = isAtBottom();
      }, { passive: true });

      function syncStyles(incomingHead) {
        if (!incomingHead) return;
        var nodes = incomingHead.querySelectorAll('style');
        for (var i = 0; i < nodes.length; i++) {
          var text = nodes[i].textContent || '';
          if (!text) continue;
          if (copiedStyleTexts.has(text)) continue;
          copiedStyleTexts.add(text);
          var clone = document.createElement('style');
          clone.textContent = text;
          document.head.appendChild(clone);
        }
      }

      function forceScrollable() {
        // Model HTML often emits "html, body { height: 100vh; overflow: hidden }"
        // for app-shell layouts. That traps tall dashboards in a fixed
        // viewport with no scrollbar. Inline styles with !important have
        // 1,0,0,0 specificity and beat any stylesheet rule — even a model
        // !important on the html selector loses to this.
        document.documentElement.style.setProperty('overflow-y', 'auto', 'important');
        document.documentElement.style.setProperty('overflow-x', 'auto', 'important');
        document.documentElement.style.setProperty('height', 'auto', 'important');
        document.documentElement.style.setProperty('width', 'auto', 'important');
        document.body.style.setProperty('overflow', 'visible', 'important');
        document.body.style.setProperty('height', 'auto', 'important');
        document.body.style.setProperty('min-height', '100%', 'important');
        // Don't pin body width to 100% — that would clip wide tables. Let
        // the body grow to fit its content; html's overflow-x:auto then
        // produces a scrollbar.
        document.body.style.setProperty('width', 'auto', 'important');
        document.body.style.setProperty('min-width', '100%', 'important');
      }

      function applyHtml(html) {
        try {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          if (!doc || !doc.body) return;
          syncStyles(doc.head);
          if (doc.body.className) document.body.className = doc.body.className;
          if (window.morphdom) {
            window.morphdom(document.body, doc.body, { childrenOnly: true });
          } else {
            document.body.innerHTML = doc.body.innerHTML;
          }
          // Re-apply after every morph: morphdom may have replaced body and
          // wiped inline styles, and any newly-synced model <style> blocks
          // are now cascaded into head.
          forceScrollable();
          // Tail the stream — keep the user pinned to the bottom as new
          // content lands, unless they scrolled up to read something
          // earlier (in which case autoScroll has already flipped false).
          if (autoScroll) {
            requestAnimationFrame(scrollToBottom);
          }
          // Tell the parent the iframe has rendered at least one frame of
          // real content so it can lift the loading skeleton. Wait for the
          // browser to actually paint (rAF + setTimeout) so the lift lines
          // up with the visible frame, not the morphdom mutation.
          if (!firstPaintSent && document.body.children.length > 0) {
            firstPaintSent = true;
            requestAnimationFrame(function () {
              setTimeout(function () {
                window.parent.postMessage({ type: 'artifact-first-paint' }, '*');
              }, 0);
            });
          }
        } catch (err) {
          // Swallow — partial HTML during streaming may not parse cleanly.
          // Next message with more content usually resolves it.
        }
      }

      window.addEventListener('message', function (e) {
        if (!e.data || e.data.type !== 'artifact-html') return;
        if (!ready) { queued = e.data.html; return; }
        applyHtml(e.data.html);
      });

      function checkReady() {
        if (window.tailwind && window.morphdom) {
          ready = true;
          if (queued != null) { applyHtml(queued); queued = null; }
          window.parent.postMessage({ type: 'artifact-bootstrap-ready' }, '*');
        } else {
          setTimeout(checkReady, 30);
        }
      }
      checkReady();
    })();
  </script>
</head>
<body></body>
</html>`;

/**
 * Stable iframe + postMessage bridge. The iframe stays mounted for the
 * lifetime of the artifact id; HTML updates arrive as messages and
 * morphdom patches the body in place.
 */
export function HtmlPreviewFrame({
  artifactId,
  html,
  title,
  throttleMs = 250,
  reloadKey = 0,
  onReady,
}: HtmlPreviewFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  /** Whether the bootstrap script has signaled it's ready to receive HTML. */
  const readyRef = useRef(false);
  /** Latest HTML we wanted to send. Flushed when ready or on timer fire. */
  const latestHtmlRef = useRef<string>(html);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Stable ref so a parent that re-renders doesn't lose the latest callback. */
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  /**
   * Send the latest queued HTML to the iframe. Safe to call before the
   * iframe is ready — the message is dropped and the bootstrap will
   * pull from `latestHtmlRef` once it signals ready.
   */
  function flushToIframe() {
    timerRef.current = null;
    if (!readyRef.current) return;
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage(
      { type: "artifact-html", html: latestHtmlRef.current },
      "*",
    );
  }

  /**
   * Listen for the bootstrap-ready signal from THIS iframe. Gated on
   * `e.source` so other iframes on the page (e.g. presentation thumbs)
   * don't trigger us by accident.
   */
  useEffect(() => {
    function handler(e: MessageEvent) {
      if (e.source !== iframeRef.current?.contentWindow) return;
      // Two signals from the iframe — keep them separate so the skeleton
      // doesn't lift before the iframe has actually painted content.
      if (e.data?.type === "artifact-bootstrap-ready") {
        readyRef.current = true;
        // `latestHtmlRef.current` is kept fresh by the `html`-prop effect
        // below, so it already reflects whatever the parent passed us
        // (create → artifact.content; update → artifact.pendingContent).
        // We intentionally do NOT pull from the store here — that would
        // override the parent's choice and flush the wrong body during
        // updates.
        flushToIframe();
        return;
      }
      if (e.data?.type === "artifact-first-paint") {
        // Iframe has rendered at least one frame of real content. Safe to
        // lift the skeleton without flashing an empty iframe.
        onReadyRef.current?.();
        return;
      }
    }
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  /**
   * Reset readiness when the artifact id OR the reload counter changes —
   * either path re-mounts the iframe via the composed React key, so a
   * fresh bootstrap handshake is required before we can resume sending
   * messages.
   */
  useEffect(() => {
    readyRef.current = false;
  }, [artifactId, reloadKey]);

  /**
   * Coalesce HTML updates onto a single throttled flush. The iframe
   * patches are cheap (morphdom diff), but coalescing tokens into one
   * render frame keeps the experience smooth on slow streams too.
   */
  useEffect(() => {
    latestHtmlRef.current = html;
    if (!readyRef.current) return;
    if (timerRef.current) return;
    timerRef.current = setTimeout(flushToIframe, throttleMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html, throttleMs]);

  /** Cleanup any pending flush when the component unmounts. */
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <iframe
      // Composed key — stable per artifact during streaming, but bumps
      // when `reloadKey` increments so the toolbar's refresh button can
      // force a clean bootstrap + re-apply.
      key={`${artifactId}:${reloadKey}`}
      ref={iframeRef}
      // We need scripts for Tailwind + morphdom + the message listener,
      // but deliberately omit `allow-same-origin` so the iframe code
      // cannot reach back into our cookies / storage / parent DOM.
      sandbox="allow-scripts"
      srcDoc={BOOTSTRAP_SRCDOC}
      title={title || "HTML artifact"}
      className="h-full w-full bg-white"
    />
  );
}
